/**
 * StorageAccessor — incognito-safe `localStorage` with an in-memory fallback.
 *
 * `localStorage` throws (or is absent) in private-mode / quota-exceeded /
 * SSR-ish contexts. This accessor never throws: it probes `localStorage` once,
 * and on any failure transparently falls back to a process-lifetime in-memory
 * `Map`. Consumers get a uniform `get`/`set`/`remove` surface and need no
 * try/catch of their own.
 *
 * Used by `model-finder/model-finder-state.ts` to persist the single
 * `selectedId` through the versioned serializer (spec §3.8).
 *
 * @see src-js/lib/storage/README.md (storage + versioned-envelope code pattern)
 */

/** The minimal read/write surface a backing store must provide. */
interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Probe whether `localStorage` is present AND writable (private mode throws). */
function probeLocalStorage(): KeyValueStore | null {
  try {
    const probeKey = '__datte_storage_probe__';
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return localStorage;
  } catch {
    return null;
  }
}

/** A process-lifetime in-memory store mirroring the `KeyValueStore` surface. */
class MemoryStore implements KeyValueStore {
  private readonly data = new Map<string, string>();
  getItem(key: string): string | null {
    return this.data.has(key) ? (this.data.get(key) as string) : null;
  }
  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }
  removeItem(key: string): void {
    this.data.delete(key);
  }
}

export class StorageAccessor {
  private readonly store: KeyValueStore;

  /** @param usingFallback exposed for tests/diagnostics — true when in memory mode. */
  readonly usingFallback: boolean;

  constructor() {
    const local = probeLocalStorage();
    this.store = local ?? new MemoryStore();
    this.usingFallback = local === null;
  }

  /** Read a key; `null` when absent or the read throws. */
  get(key: string): string | null {
    try {
      return this.store.getItem(key);
    } catch {
      return null;
    }
  }

  /** Write a key; silently no-ops on failure (quota, etc.). */
  set(key: string, value: string): void {
    try {
      this.store.setItem(key, value);
    } catch {
      // unavailable / quota — fail silently (the whole point of this accessor).
    }
  }

  /** Remove a key; silently no-ops on failure. */
  remove(key: string): void {
    try {
      this.store.removeItem(key);
    } catch {
      // fail silently.
    }
  }
}
